import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { t } from "../../i18n";
import { getLocalizedDisplay } from "../../utils/localizedDisplay";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebookF, FaInstagram, FaYoutube, FaTwitter, FaLinkedinIn } from "react-icons/fa";
import "./Footer.css";

export default function Footer() {
  const { state } = useContext(ApplicationContext);
  const brand = state.brand || state.client;
  const locale = state.locale || 'en';
  const prefix = locale === 'en' ? '' : `/${locale}`;
  const brandKey = brand.brandKey || 'gso';
  const services = state.taxonomy.dimensions.find((d) => d.id === "service")?.values || [];
  const allLocations = state.taxonomy.dimensions.find((d) => d.id === "location")?.values || [];
  const physicalSiteSlugs = brand.physicalSiteSlugs;
  const locations = (physicalSiteSlugs && physicalSiteSlugs.length
    ? allLocations.filter((l) => physicalSiteSlugs.includes(l.slug))
        .sort((a, b) => physicalSiteSlugs.indexOf(a.slug) - physicalSiteSlugs.indexOf(b.slug))
    : allLocations.filter((l) => l.brand === brandKey));
  const year = new Date().getFullYear();

  // Brand-specific social links scraped from production sites.
  // GSO  → gaspineortho.com  | Apex → apexosn.com (NC + TN share Apex socials)
  const SOCIALS = {
    gso: [
      { label: "Facebook",  href: "https://www.facebook.com/GeorgiaSpineOrtho",                          Icon: FaFacebookF },
      { label: "Instagram", href: "https://www.instagram.com/georgiaspine?igsh=MWx3MWlka2l2c3R4dw==",     Icon: FaInstagram },
      { label: "YouTube",   href: "https://www.youtube.com/channel/UC00xE8_odb8bds9QFXBIN9g",            Icon: FaYoutube },
      { label: "Twitter",   href: "https://twitter.com/gaspineortho1",                                  Icon: FaTwitter },
      { label: "LinkedIn",  href: "https://www.linkedin.com/company/georgiaspineandorthopaedics/",       Icon: FaLinkedinIn },
    ],
    "apex-nc": [
      { label: "Facebook", href: "https://www.facebook.com/ApexOSN", Icon: FaFacebookF },
    ],
    "apex-tn": [
      { label: "Facebook", href: "https://www.facebook.com/ApexOSN", Icon: FaFacebookF },
    ],
  };
  const socials = SOCIALS[brandKey] || [];

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          {/* Brand */}
          <div className="footer-section">
            <h3 className="footer-brand">{brand.name}</h3>
            <p className="footer-tagline">{t("site.tagline", locale)}</p>
            <div className="footer-contact">
              <a href={`tel:${brand.phone}`}>
                <FaPhone /> {brand.phoneExisting ? t('phone.new', locale, { phone: brand.phone }) : brand.phone}
              </a>
              {brand.phoneExisting && (
                <a href={`tel:${brand.phoneExisting}`}>
                  <FaPhone /> {t('phone.existing', locale, { phone: brand.phoneExisting })}
                </a>
              )}
              <a href={`mailto:${brand.email}`}>
                <FaEnvelope /> {brand.email}
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="footer-section">
            <h4>{t("footer.allServices", locale)}</h4>
            <ul>
              {services.slice(0, 10).map((s) => (
                <li key={s.slug}>
                  <Link to={`${prefix}/${locations[0]?.slug || "atlanta"}/${s.slug}/`}>
                    {getLocalizedDisplay(s, locale)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div className="footer-section">
            <h4>{t("footer.allLocations", locale)}</h4>
            <ul>
              {locations.map((l) => (
                <li key={l.slug}>
                  <Link to={`${prefix}/${l.slug}/`}>
                    <FaMapMarkerAlt /> {l.city}, {l.state}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="footer-section">
            <h4>{t('footer.legal', locale)}</h4>
            <ul>
              <li><Link to={`${prefix}/privacy/`}>{t("footer.privacy", locale)}</Link></li>
              <li><Link to={`${prefix}/terms/`}>{t("footer.terms", locale)}</Link></li>
              <li><Link to={`${prefix}/ada/`}>{t("footer.ada", locale)}</Link></li>
              <li><Link to={`${prefix}/sitemap/`}>{t("footer.sitemap", locale) || "Sitemap"}</Link></li>
            </ul>
            <a
              href="https://mycertifiedorthopaedicsurgeon.org"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-cert-badge"
              aria-label={t('footer.boardCertified', locale)}
            >
              <img
                src="/assets/footer/abos-board-certified.svg"
                alt={t('footer.boardCertified', locale)}
                loading="lazy"
                width="100"
                height="150"
              />
            </a>
          </div>
        </div>

        <div className="footer-bottom">
          {socials.length > 0 && (
            <div className="footer-socials" aria-label={t('footer.socialMedia', locale)}>
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="footer-social-icon"
                >
                  <Icon />
                </a>
              ))}
            </div>
          )}
          <p className="footer-insurance-notice">
            *{t('cta.insuranceNotice', locale)}
          </p>
          <p>{t("footer.copyright", locale, { year, clientName: brand.name })}</p>
        </div>
      </div>
    </footer>
  );
}
